// packages import
import { FC, Fragment } from 'react'
import { TableBody, TableCell, TableHead, TableRow, styled } from '@mui/material'
import { format } from 'date-fns'

// component import
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

// others
import { enumToLabel, formatCurrency } from '../../../utils'
import { Link } from 'react-router-dom'
import palette from '../../../theme/palette'
import { JournalEntriesListingProps } from '../../../interfaceTypes/journalEntries'
import { JournalEntryMenu } from '../../menus/JournalEntry'
import { JournalEntryType } from '../../../../generated'

const CustomTableCell = styled(TableCell)<{ showBorder?: boolean }>(({ showBorder }) => ({
  border: showBorder ? undefined : 'none'
}))

const CustomLink = styled(Link)(() => ({
  color: palette.primary.main,
  textDecoration: 'underline'
}))

export const JournalEntriesListing: FC<JournalEntriesListingProps> = ({
  journalEntries,
  loading,
  refetch,
  onDuplicate
}): JSX.Element => {
  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} />} height="400px">
      <TableHead>
        <TableRow>
          <TableCell width="5%"> Ref no </TableCell>
          <TableCell width="5%"> Adv Ref no </TableCell>
          <TableCell width="7%"> Date </TableCell>
          <TableCell width="10%"> Account Type </TableCell>
          <TableCell width="15%"> Accounts </TableCell>
          <TableCell width="8%"> Debit </TableCell>
          <TableCell width="8%"> Credit </TableCell>
          <TableCell width="5%"> Bill# </TableCell>
          <TableCell width="5%"> Matter# </TableCell>
          <TableCell width="5%"> Vendor# </TableCell>
          <TableCell width="12%"> Profit Center </TableCell>
          <TableCell width="15%"> Notes </TableCell>
          <TableCell width="3%"> Actions </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={12} />
      ) : (
        <TableBody>
          {journalEntries?.map(entry => {
            const {
              id,
              date,
              lineItems,
              notes,
              bill,
              case: litigation,
              vendor,
              journalEntryNumber,
              advJournalRef
            } = entry || {}

            return (
              <Fragment key={id}>
                {lineItems?.map((item, index) => {
                  const { account, amount, type, office, id } = item || {}
                  const showBorder = index === lineItems?.length - 1
                  const showJournalEntryDetail = index === 0

                  return (
                    <TableRow key={id}>
                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && journalEntryNumber}
                      </CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && advJournalRef}
                      </CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && format(new Date(date), 'MM/dd/yyyy')}
                      </CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>{enumToLabel(account?.category || '')}</CustomTableCell>

                      <CustomTableCell showBorder={showBorder}>
                        <CustomLink to={`/accounts/chartered/${account?.id}`}>
                          {account?.sortCode && `#${account?.sortCode} - `}
                          {account?.name}
                        </CustomLink>
                      </CustomTableCell>

                      <CustomTableCell showBorder={showBorder}>
                        {type === JournalEntryType.Debit && formatCurrency(amount || 0)}
                      </CustomTableCell>

                      <CustomTableCell showBorder={showBorder}>
                        {type === JournalEntryType.Credit && formatCurrency(amount || 0)}
                      </CustomTableCell>

                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && (
                          <CustomLink to={`/billing/bill-detail/${bill?.id}`}>{bill?.id && `#${bill?.id}`}</CustomLink>
                        )}
                      </CustomTableCell>

                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && (
                          <CustomLink to={`/matters/matter-detail/${litigation?.id}`}>
                            {litigation?.caseNumber && `#${litigation?.caseNumber}`}
                          </CustomLink>
                        )}
                      </CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>
                        {showJournalEntryDetail && vendor?.vendorId && `#${vendor?.vendorId}`}
                      </CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>{office?.name}</CustomTableCell>
                      <CustomTableCell showBorder={showBorder}>{showJournalEntryDetail && notes}</CustomTableCell>
                      <CustomTableCell showBorder={showBorder} align="right">
                        {showJournalEntryDetail && (
                          <JournalEntryMenu journalEntry={entry} onRefetch={refetch} onDuplicate={onDuplicate} />
                        )}
                      </CustomTableCell>
                    </TableRow>
                  )
                })}
              </Fragment>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}
